import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { Plus, Search, Pencil, Trash2, ShieldCheck, UserCircle, AlertCircle } from 'lucide-react';
import api from '../../services/apiClient.ts';
import { formatDate } from '../../utils/date';

const ManageUsers = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const roleOptions = useMemo(() => ([
    { value: 'admin', label: t('users.roles.admin') },
    { value: 'manager', label: t('users.roles.manager') },
    { value: 'cashier', label: t('users.roles.cashier') },
    { value: 'viewer', label: t('users.roles.viewer') },
  ]), [t]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'ON' | 'OFF'

  const [formOpen, setFormOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', status: 'ON', branch_id: null });

  // Fetch users from API on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/v1/users');
      const data = response.data?.data || [];
      // Transform API response to include role and permissions for UI
      const transformedUsers = data.map(u => ({
        ...u,
        role: 'viewer', // Default role mapping
        active: u.status === 'ON',
        permissions: new Set(), // Empty - will be populated via permissions modal
      }));
      setUsers(transformedUsers);
    } catch (err) {
      setError(err.message || 'Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const availablePermissions = useMemo(() => ([
    {
      key: 'core',
      title: t('users.categories.core'),
      items: [
        { key: 'home', label: t('users.pages.home') },
        { key: 'createGroup', label: t('users.pages.createGroup') },
        { key: 'SalesPage', label: t('users.pages.SalesPage') },
        { key: 'PaymentsPage', label: t('users.pages.PaymentsPage') },
        { key: 'receiveCash', label: t('users.pages.receiveCash') },
      ]
    },
    {
      key: 'groups',
      title: t('users.categories.groups'),
      items: [
        { key: 'manageGroups', label: t('users.pages.manageGroups') },
        { key: 'AgentsPage', label: t('users.pages.AgentsPage') },
        { key: 'TravelersPage', label: t('users.pages.TravelersPage') },
        { key: 'SupervisorsPage', label: t('users.pages.SupervisorsPage') },
        { key: 'GroupsPaymentPage', label: t('users.pages.GroupsPaymentPage') },
      ]
    },
    {
      key: 'reports',
      title: t('users.categories.reports'),
      items: [
        { key: 'balances', label: t('users.pages.balances') },
        { key: 'Balancestravelers', label: t('users.pages.Balancestravelers') },
        { key: 'Balancesagents', label: t('users.pages.Balancesagents') },
        { key: 'Balancessupervisors', label: t('users.pages.Balancessupervisors') },
        { key: 'accountStatement', label: t('users.pages.accountStatement') },
        { key: 'TravelersLedger', label: t('users.pages.TravelersLedger') },
        { key: 'InstallmentsQuery', label: t('users.pages.InstallmentsQuery') },
        { key: 'groupReport', label: t('users.pages.groupReport') },
        { key: 'activegroups', label: t('users.pages.activegroups') },
      ]
    },
    {
      key: 'system',
      title: t('users.categories.system'),
      items: [
        { key: 'dailySchedule', label: t('users.pages.dailySchedule') },
        { key: 'reports', label: t('users.pages.reports') },
        { key: 'manageUsers', label: t('users.pages.manageUsers') },
        { key: 'manageExpenses', label: t('users.pages.manageExpenses') },
        { key: 'payments', label: t('users.pages.payments') },
      ]
    },
  ]), [t]);

  const filteredUsers = useMemo(() => {
    let list = [...users];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }
    if (roleFilter !== 'all') list = list.filter(u => u.role === roleFilter);
    if (statusFilter !== 'all') list = list.filter(u => u.status === statusFilter);
    return list;
  }, [users, searchQuery, roleFilter, statusFilter]);

  const openAdd = () => {
    setEditingUser(null);
    setForm({ name: '', email: '', password: '', status: 'ON' });
    setFormOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: '', status: u.status });
    setFormOpen(true);
  };

  const saveForm = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    try {
      setSaving(true);
      setError(null);

      const payload = { ...form };
      // Add branch_id from localStorage (hidden from user)
      const branchId = localStorage.getItem('branch');
      if (branchId) {
        payload.branch_id = parseInt(branchId);
      }
      if (!payload.password) delete payload.password; // Only include password if set

      if (editingUser) {
        await api.put(`/v1/users/${editingUser.id}`, payload);
      } else {
        if (!form.password) {
          setError('Password is required for new users');
          setSaving(false);
          return;
        }
        await api.post('/v1/users', payload);
      }

      setToast({ type: 'success', message: editingUser ? 'User updated successfully' : 'User created successfully' });
      setFormOpen(false);
      setSaving(false); // Reset saving state after successful save
      await fetchUsers();
    } catch (err) {
      setSaving(false);
      // Extract validation errors from 422 response
      let errorMsg = 'Failed to save user';
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorList = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        errorMsg = errorList || 'Validation failed';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setToast({ type: 'error', message: errorMsg });
      console.error('Error saving user:', err);
    }
  };

  // Clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const confirmDelete = async (u) => {
    if (!window.confirm(isRTL ? 'هل تريد حذف المستخدم؟' : 'Delete this user?')) return;

    try {
      setError(null);
      await api.delete(`/v1/users/${u.id}`);
      setToast({ type: 'success', message: 'User deleted successfully' });
      await fetchUsers();
    } catch (err) {
      const errorMsg = err.message || 'Failed to delete user';
      setToast({ type: 'error', message: errorMsg });
      console.error('Error deleting user:', err);
    }
  };

  // Permissions modal state
  const [permState, setPermState] = useState({});
  const openPermissions = (u) => {
    const map = {};
    availablePermissions.forEach(cat => cat.items.forEach(it => { map[it.key] = u.permissions?.has?.(it.key) || false; }));
    setPermState(map);
    setEditingUser(u);
    setPermOpen(true);
  };
  const togglePerm = (key) => setPermState(prev => ({ ...prev, [key]: !prev[key] }));
  const setAllPerms = (val) => {
    const map = {};
    availablePermissions.forEach(cat => cat.items.forEach(it => { map[it.key] = !!val; }));
    setPermState(map);
  };
  const savePermissions = () => {
    if (!editingUser) return;
    const granted = new Set(Object.keys(permState).filter(k => permState[k]));
    setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, permissions: granted } : u));
    setPermOpen(false);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('users.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('users.subtitle')}</p>
        </div>
        <button onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-soft">
          <Plus className="w-4 h-4" />
          {t('users.addUser')}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <div className={`fixed top-4 ${isRTL ? 'right-4' : 'left-4'} z-50 max-w-sm animate-in`}>
          <div className={`p-4 rounded-lg shadow-lg flex items-center gap-3 ${
            toast.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            <AlertCircle className={`w-5 h-5 ${
              toast.type === 'success'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`} />
            <span className={`text-sm ${
              toast.type === 'success'
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-700 dark:text-red-300'
            }`}>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute top-3 left-3 rtl:right-3 rtl:left-auto" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('users.searchPlaceholder')}
            className="w-full pl-9 rtl:pl-3 rtl:pr-9 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">{t('common.all')}</option>
            {roleOptions.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setStatusFilter('all')} className={`px-3 py-2 rounded-lg border ${statusFilter==='all' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>{t('common.all')}</button>
          <button onClick={() => setStatusFilter('ON')} className={`px-3 py-2 rounded-lg border ${statusFilter==='ON' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>{t('users.active')}</button>
          <button onClick={() => setStatusFilter('OFF')} className={`px-3 py-2 rounded-lg border ${statusFilter==='OFF' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>{t('users.inactive')}</button>
        </div>
      </div>

      {/* Table */}
      <div className="app-table-wrap shadow-soft">
        <table className="app-table min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t('common.index')}</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t('users.name')}</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t('users.email')}</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">Branch ID</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t('users.status')}</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">Created At</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-400">Loading...</td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-400">{t('users.noUsers')}</td>
              </tr>
            ) : (
              filteredUsers.map((u, idx) => (
                <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right rtl:text-right">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-gray-400" />
                    <span>{u.name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right rtl:text-right">{u.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right rtl:text-right">{u.branch_id || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right rtl:text-right">
                    {u.status === 'ON' ? (
                      <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">{t('users.active')}</span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">{t('users.inactive')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right rtl:text-right">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right rtl:text-right">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(u)} className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-1">
                        <Pencil className="w-4 h-4" /> {t('common.edit')}
                      </button>
                      <button onClick={() => openPermissions(u)} className="px-3 py-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800 flex items-center gap-1">
                        <ShieldCheck className="w-4 h-4" /> {t('users.permissions')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFormOpen(false)} />
          <div className="relative mx-auto mt-24 w-[95%] max-w-xl bg-white dark:bg-gray-900 rounded-2xl shadow-medium">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{editingUser ? t('users.editUser') : t('users.addUser')}</h3>
              <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" onClick={() => setFormOpen(false)}>×</button>
            </div>
            <form onSubmit={saveForm} className="px-5 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">{t('users.name')}</label>
                  <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">{t('users.email')}</label>
                  <input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">Password {editingUser && '(اترك الحقل فارغ للحفاظ على الباسوورد الحالي)'}</label>
                  <input type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" required={!editingUser} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">{t('users.status')}</label>
                  <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                    <option value="ON">{t('users.active')}</option>
                    <option value="OFF">{t('users.inactive')}</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={()=>setFormOpen(false)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300">{t('common.cancel')}</button>
                <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">{saving ? 'Saving...' : t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPermOpen(false)} />
          <div className="relative mx-auto mt-20 w-[96%] max-w-4xl bg-white dark:bg-gray-900 rounded-2xl shadow-medium">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.managePermissions')}</h3>
              <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" onClick={() => setPermOpen(false)}>×</button>
            </div>
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">{editingUser?.name} — {editingUser?.email}</div>
                <div className="flex items-center gap-2">
                  <button onClick={()=>setAllPerms(true)} className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{t('users.selectAll')}</button>
                  <button onClick={()=>setAllPerms(false)} className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{t('users.deselectAll')}</button>
                </div>
              </div>
              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-1">
                {availablePermissions.map(cat => (
                  <div key={cat.key} className="border rounded-xl border-gray-200 dark:border-gray-800">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-t-xl font-medium text-gray-900 dark:text-gray-100">{cat.title}</div>
                    <div className="px-4 py-3 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {cat.items.map(it => (
                        <label key={it.key} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                          <input type="checkbox" checked={!!permState[it.key]} onChange={()=>togglePerm(it.key)} />
                          <span>{it.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={()=>setPermOpen(false)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300">{t('common.cancel')}</button>
                <button type="button" onClick={savePermissions} className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700">{t('common.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;